import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MEMBER_FILTER_LABEL, PERMISSION, can, formatIst, inboxApi, useAuth,
  type MemberAction, type MemberFilter, type MemberQuery, type Segment, type SegmentMember,
} from '@wa/shared';
import { Avatar } from '@/components/Avatar';
import { ListTargetDialog, saveCsv, type ListTarget } from '@/broadcast/ListTargetDialog';

/**
 * Who is in the list — the reference's right-hand column, with the things a
 * list owner does to it: look at who has opted out, dropped off WhatsApp or
 * never written back, and remove, move or copy them in one go.
 *
 * This is the same query the campaign engine freezes its audience from, so
 * "Reachable" is who would be sent to, rule-based lists included; the other
 * filters widen it to the people a send leaves out, which is exactly who
 * somebody cleaning a list wants to see. Paged by name on scroll.
 *
 * The outcome of a *broadcast* (failed, unread, replied) is not a filter
 * here on purpose: it belongs to one send, not to the list, and lives on the
 * report with the same bulk actions.
 *
 * "Add a member" is a search over contacts, not a form: the person already
 * exists, and the only thing to decide is which one.
 */
export function MembersPanel({ segment, onClose }: { segment: Segment; onClose?: () => void }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const mayWrite = can(user, PERMISSION.segmentsWrite);
  const mayFlag = can(user, PERMISSION.contactsWrite);
  const mayExport = can(user, PERMISSION.contactsExport);

  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filter, setFilter] = useState<MemberFilter>('reachable');
  const [tag, setTag] = useState<number | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [more, setMore] = useState(false);
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [target, setTarget] = useState<'copy' | 'move' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Changing what is shown changes what "all" means; the selection is cleared
  // rather than carried into a set it no longer describes.
  useEffect(() => { setPicked(new Set()); setAllMatching(false); }, [filter, tag, from, to, debounced, segment.id]);

  const query: MemberQuery = useMemo(
    () => ({ filter, q: debounced || null, tag, from: from || null, to: to || null }),
    [filter, debounced, tag, from, to],
  );

  const members = useInfiniteQuery({
    queryKey: ['segment-members', segment.id, query],
    queryFn: ({ pageParam }) => inboxApi.segmentMembers(segment.id, query, pageParam),
    initialPageParam: null as { id: number; name: string | null } | null,
    getNextPageParam: (last) => (last.nextAfterId === null ? null : { id: last.nextAfterId, name: last.nextAfterName }),
  });
  const tags = useQuery({ queryKey: ['tags'], queryFn: inboxApi.tags, staleTime: 300_000 });

  const rows = members.data?.pages.flatMap((p) => p.items) ?? [];
  const isStatic = segment.kind === 0;
  const selectedCount = allMatching ? null : picked.size;
  const anySelected = allMatching || picked.size > 0;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['segment-members', segment.id] });
    void queryClient.invalidateQueries({ queryKey: ['segments'] });
  };

  const act = useMutation({
    mutationFn: (body: { action: MemberAction } & Partial<ListTarget>) => inboxApi.memberActions(segment.id, {
      action: body.action,
      ...(allMatching ? { all: true, ...query } : { contactIds: [...picked] }),
      targetSegmentId: 'targetSegmentId' in body ? body.targetSegmentId : null,
      newListName: 'newListName' in body ? body.newListName : null,
    }),
    onSuccess: (result, body) => {
      setError(null);
      setTarget(null);
      setPicked(new Set());
      setAllMatching(false);
      const n = result.affected.toLocaleString('en-IN');
      setNotice(
        body.action === 'remove' ? `${n} removed from this list.`
          : body.action === 'copy' ? `${n} added to the other list.`
            : body.action === 'move' ? `${n} moved.`
              : `${n} can be messaged again.`,
      );
      setTimeout(() => setNotice(null), 4000);
      refresh();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'That did not work.'),
  });

  const remove = useMutation({
    mutationFn: (contactId: number) => inboxApi.removeSegmentMember(segment.id, contactId),
    onSuccess: refresh,
  });

  const exportCsv = async () => {
    try {
      const { blob, fileName } = await inboxApi.exportMembers(segment.id, query);
      saveCsv(blob, fileName || `${segment.name}.csv`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not export.');
    }
  };

  const toggle = (id: number) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const filters: MemberFilter[] = ['reachable', 'optedOut', 'blocked', 'noWhatsapp', 'neverReplied', 'all'];

  return (
    <aside className="flex h-full min-h-0 flex-col border-l border-(--color-line) bg-(--color-surface)" aria-label="Members">
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-(--color-line) px-3">
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Hide members" className="tap -ml-1 grid size-9 place-items-center rounded-full text-(--color-muted) hover:bg-(--color-sunken)">
            <IconClose />
          </button>
        )}
        <IconSearch />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Name or number"
          aria-label="Search for members"
          className="min-w-0 flex-1 bg-transparent text-body outline-none placeholder:text-(--color-faint)"
        />
        <button
          type="button" onClick={() => setMore((v) => !v)} aria-expanded={more} aria-label="More filters"
          className={`tap grid size-8 place-items-center rounded-full ${more || tag || from || to ? 'bg-(--color-brand-wash) text-(--color-brand)' : 'text-(--color-muted) hover:bg-(--color-sunken)'}`}
        >
          <IconFilter />
        </button>
      </div>

      {/* Who is shown. One row of chips, scrollable: the default is who a send
          reaches, the rest are the people a send leaves out. */}
      <div className="chip-scroll flex shrink-0 gap-1 overflow-x-auto border-b border-(--color-line-dim) px-2 py-1.5">
        {filters.map((f) => (
          <button
            key={f} type="button" onClick={() => setFilter(f)} aria-pressed={filter === f}
            className={`tap shrink-0 rounded-full px-2.5 py-1 text-micro font-medium ${filter === f ? 'bg-(--color-brand-wash) text-(--color-brand)' : 'text-(--color-muted) hover:bg-(--color-sunken)'}`}
          >
            {MEMBER_FILTER_LABEL[f]}
          </button>
        ))}
      </div>

      {more && (
        <div className="flex shrink-0 flex-col gap-2 border-b border-(--color-line-dim) px-3 py-2">
          <label className="flex items-center gap-2 text-small">
            <span className="w-14 shrink-0 text-micro text-(--color-muted)">Tag</span>
            <select
              value={tag ?? ''} onChange={(e) => setTag(e.target.value ? Number(e.target.value) : null)}
              className="h-8 min-w-0 flex-1 rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2 text-small"
            >
              <option value="">Any</option>
              {(tags.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-small">
            <span className="w-14 shrink-0 text-micro text-(--color-muted)">{isStatic ? 'Added' : 'Created'}</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" className="h-8 min-w-0 flex-1 rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2 text-small" />
            <span className="text-micro text-(--color-muted)">to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" className="h-8 min-w-0 flex-1 rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2 text-small" />
          </label>
          {(tag || from || to) && (
            <button type="button" onClick={() => { setTag(null); setFrom(''); setTo(''); }} className="tap self-start text-micro font-medium text-(--color-brand) hover:underline">Clear</button>
          )}
        </div>
      )}

      {(notice || error) && (
        <p role={error ? 'alert' : 'status'} className={`shrink-0 px-3 py-1.5 text-small ${error ? 'text-(--color-bad)' : 'text-(--color-good)'}`}>{error ?? notice}</p>
      )}

      {/* The bulk bar: what is selected and what can be done with it. "All" is
          server-side — every member the filter shows, not the rows loaded so far. */}
      {anySelected && (
        <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-(--color-line) bg-(--color-brand-wash) px-3 py-2">
          <span className="mr-auto text-small font-medium">
            {allMatching ? `Everyone shown` : `${selectedCount} selected`}
          </span>
          {!allMatching && (
            <button type="button" onClick={() => setAllMatching(true)} className="tap text-micro font-medium text-(--color-brand) hover:underline">Select all shown</button>
          )}
          {isStatic && mayWrite && (
            <Act label="Remove" tone="bad" busy={act.isPending} onClick={() => { if (window.confirm(allMatching ? 'Remove everyone this filter shows from the list?' : `Remove ${picked.size} from this list?`)) act.mutate({ action: 'remove' }); }} />
          )}
          {mayWrite && <Act label="Copy to…" busy={act.isPending} onClick={() => setTarget('copy')} />}
          {isStatic && mayWrite && <Act label="Move to…" busy={act.isPending} onClick={() => setTarget('move')} />}
          {filter === 'noWhatsapp' && mayFlag && (
            <Act label="Clear flag" busy={act.isPending} onClick={() => act.mutate({ action: 'clearNoWhatsapp' })} />
          )}
          <button type="button" onClick={() => { setPicked(new Set()); setAllMatching(false); }} aria-label="Clear selection" className="tap grid size-7 place-items-center rounded-full text-(--color-muted) hover:bg-(--color-surface)"><IconClose /></button>
        </div>
      )}

      <ul
        className="pane-scroll min-h-0 flex-1 overflow-y-auto"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 200 && members.hasNextPage && !members.isFetchingNextPage) {
            void members.fetchNextPage();
          }
        }}
      >
        {members.isPending && <li className="px-4 py-10 text-center text-small text-(--color-muted)">Loading</li>}
        {members.isError && (
          <li className="px-6 py-10 text-center text-small text-(--color-bad)">Could not load the members of this list.</li>
        )}
        {!members.isPending && !members.isError && rows.length === 0 && (
          <li className="px-6 py-16 text-center text-body text-(--color-faint)">
            {filter === 'reachable' && !debounced && !tag && !from && !to ? 'No members found' : 'Nobody matches this filter'}
          </li>
        )}
        {rows.map((m) => (
          <MemberRow
            key={m.id} member={m} isStatic={isStatic} mayWrite={mayWrite}
            checked={allMatching || picked.has(m.id)}
            onToggle={() => { if (allMatching) { setAllMatching(false); setPicked(new Set(rows.filter((r) => r.id !== m.id).map((r) => r.id))); } else toggle(m.id); }}
            onRemove={() => remove.mutate(m.id)}
          />
        ))}
        {members.isFetchingNextPage && <li className="px-4 py-3 text-center text-small text-(--color-muted)">Loading more…</li>}
      </ul>

      <footer className="shrink-0 border-t border-(--color-line)">
        {adding ? (
          <AddMember segmentId={segment.id} onDone={() => setAdding(false)} />
        ) : (
          <div className="flex items-center">
            {isStatic && mayWrite && (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="tap flex flex-1 items-center justify-center gap-2 px-3 py-3 text-body font-medium text-(--color-ink) hover:bg-(--color-sunken)"
              >
                <IconAddPerson /> Add a member
              </button>
            )}
            {mayExport && (
              <button type="button" onClick={() => void exportCsv()} title="Download what this filter shows as a CSV" className="tap flex items-center gap-1.5 px-3 py-3 text-small font-medium text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)">
                <IconDownload /> CSV
              </button>
            )}
          </div>
        )}
      </footer>

      {target && (
        <ListTargetDialog
          title={target === 'move' ? 'Move to which list?' : 'Copy to which list?'}
          subtitle={allMatching ? 'Everyone the current filter shows.' : `${picked.size} ${picked.size === 1 ? 'person' : 'people'}.`}
          excludeId={segment.id}
          busy={act.isPending}
          error={act.isError ? error : null}
          onPick={(t) => act.mutate({ action: target, ...t })}
          onClose={() => setTarget(null)}
        />
      )}
    </aside>
  );
}

function Act({ label, tone, busy, onClick }: { label: string; tone?: 'bad'; busy: boolean; onClick: () => void }) {
  return (
    <button
      type="button" disabled={busy} onClick={onClick}
      className={`tap rounded-(--radius-field) border px-2.5 py-1 text-micro font-medium disabled:opacity-50 ${tone === 'bad' ? 'border-(--color-bad)/40 text-(--color-bad) hover:bg-(--color-bad)/8' : 'border-(--color-line) bg-(--color-surface) hover:bg-(--color-sunken)'}`}
    >
      {label}
    </button>
  );
}

function MemberRow({ member: m, isStatic, mayWrite, checked, onToggle, onRemove }: {
  member: SegmentMember; isStatic: boolean; mayWrite: boolean; checked: boolean; onToggle: () => void; onRemove: () => void;
}) {
  // The one thing worth saying under a name: why a send would not reach them.
  const flag = m.blockedAt ? 'Blocked'
    : m.optedOutAt ? 'Opted out'
      : m.notOnWhatsAppAt ? `Not on WhatsApp · ${formatIst(m.notOnWhatsAppAt)}`
        : null;
  return (
    <li className={`group flex items-center gap-2.5 border-b border-(--color-line-dim) px-3 py-2 ${checked ? 'bg-(--color-brand-wash)/60' : ''}`}>
      <input
        type="checkbox" checked={checked} onChange={onToggle} aria-label={`Select ${m.displayName ?? m.waId}`}
        className={`size-4 shrink-0 accent-(--color-brand) ${checked ? '' : 'opacity-40 group-hover:opacity-100'}`}
      />
      <Avatar name={m.displayName} waId={m.waId} size={36} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body font-medium">{m.displayName ?? `+${m.waId}`}</span>
        <span className="block truncate text-small text-(--color-muted) tabular-nums">
          +{m.waId}
          {flag && <span className="text-(--color-bad)"> · {flag}</span>}
          {!flag && m.lastInboundAt && <span> · replied {formatIst(m.lastInboundAt)}</span>}
        </span>
      </span>
      {isStatic && mayWrite && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${m.displayName ?? m.waId} from this list`}
          className="tap grid size-8 place-items-center rounded-full text-(--color-faint) opacity-0 transition hover:bg-(--color-sunken) hover:text-(--color-bad) group-hover:opacity-100 focus-visible:opacity-100"
        >
          <IconClose />
        </button>
      )}
    </li>
  );
}

function AddMember({ segmentId, onDone }: { segmentId: number; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(t); }, [q]);

  const found = useQuery({
    queryKey: ['contacts', 'pick', debounced],
    queryFn: () => inboxApi.contacts(new URLSearchParams({ q: debounced })),
    enabled: debounced.length >= 2,
  });

  const add = useMutation({
    mutationFn: (contactId: number) => inboxApi.addSegmentMembers(segmentId, [contactId]),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['segment-members', segmentId] });
      void queryClient.invalidateQueries({ queryKey: ['segments'] });
      onDone();
    },
  });

  return (
    <div className="p-2">
      <div className="flex items-center gap-2 rounded-(--radius-field) border border-(--color-brand) px-2">
        <IconSearch />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') onDone(); }}
          placeholder="Name or number"
          aria-label="Find a contact to add"
          className="h-9 min-w-0 flex-1 bg-transparent text-body outline-none placeholder:text-(--color-faint)"
        />
        <button type="button" onClick={onDone} aria-label="Cancel" className="tap grid size-8 place-items-center text-(--color-muted)"><IconClose /></button>
      </div>
      {debounced.length >= 2 && (
        <ul className="mt-1 max-h-56 overflow-y-auto rounded-(--radius-field) border border-(--color-line)">
          {(found.data?.items ?? []).slice(0, 8).map((c) => (
            <li key={c.id}>
              <button
                type="button"
                disabled={add.isPending}
                onClick={() => add.mutate(c.id)}
                className="tap flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-(--color-sunken)"
              >
                <Avatar name={c.displayName} waId={c.waId} size={28} />
                <span className="min-w-0 flex-1 truncate text-body">{c.displayName ?? `+${c.waId}`}</span>
                <span className="text-small text-(--color-muted) tabular-nums">+{c.waId}</span>
              </button>
            </li>
          ))}
          {found.data && found.data.items.length === 0 && (
            <li className="px-3 py-2 text-small text-(--color-muted)">Nobody matches.</li>
          )}
        </ul>
      )}
    </div>
  );
}

const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const IconSearch = () => <svg viewBox="0 0 20 20" className="size-4.5 shrink-0 text-(--color-faint)" aria-hidden="true"><circle cx="8.8" cy="8.8" r="5.2" {...s} /><path d="m12.8 12.8 3.6 3.6" {...s} /></svg>;
const IconClose = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" {...s} /></svg>;
const IconFilter = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M3 5h14M6 10h8M8.5 15h3" {...s} /></svg>;
const IconDownload = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M10 3v9M6.5 8.5 10 12l3.5-3.5M4 14v2.5h12V14" {...s} /></svg>;
const IconAddPerson = () => <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><circle cx="8" cy="7" r="3" {...s} /><path d="M2.5 16a5.5 5.5 0 0 1 11 0M15 8v5M12.5 10.5h5" {...s} /></svg>;
